import { useCallback, useMemo, useState } from 'react'
import { AdminAPI, getFragment, GraphQL, SharedAPI } from '@guestgoodie/graphql'
import debounce from 'lodash.debounce'
import { useQuery } from '@apollo/client'
import { useLingui } from '@lingui/react/macro'

import ComboBox from '@/components/ui/ComboBox'
import usePaginatedStays, { QueryVariables } from '../hooks/usePaginatedStays'

interface Props {
  id?: number | null
  placeholder?: string
  onChange: (id: number | null) => void
  name?: string
  invalid?: boolean
}

function stayToOption(stay: GraphQL.StayFragmentFragment) {
  const space = getFragment(SharedAPI.space.SPACE_FRAGMENT, stay.space)
  const firstGuest = getFragment(
    SharedAPI.guest.GUEST_FRAGMENT,
    stay.guests?.[0]
  )

  const guestName = firstGuest
    ? `${firstGuest.firstName} ${firstGuest.lastName}`
    : null

  const spaceDesc = space?.identifier ?? ''

  return {
    value: `${stay.id}`,
    label: guestName ? `${guestName} (${spaceDesc})` : spaceDesc
  }
}

export function SelectStay({
  id,
  placeholder,
  onChange,
  name,
  invalid = false
}: Props) {
  const { t } = useLingui()
  const [search, setSearch] = useState<string>('')
  const [debouncedSearch, setDebouncedSearch] = useState<string>('')

  const variables = useMemo<QueryVariables>(
    () => ({
      filter: {
        search: debouncedSearch
      },
      sort: {
        direction: GraphQL.SortDirection.Asc,
        field: GraphQL.StaySortField.ScheduledStart
      }
    }),
    [debouncedSearch]
  )

  const { data } = useQuery(AdminAPI.stay.GET, {
    variables: { id: id ?? 0 },
    skip: !id
  })

  const stay = useMemo(
    () => getFragment(SharedAPI.stay.STAY_FRAGMENT, data?.getStay),
    [data]
  )

  const updateDebouncedSearch = useMemo(
    () => debounce(setDebouncedSearch, 500),
    [setDebouncedSearch]
  )

  const onSearch = useCallback(
    (newValue: string) => {
      setSearch(newValue)
      updateDebouncedSearch(newValue)
    },
    [setSearch, updateDebouncedSearch]
  )

  const { items: stays, loading } = usePaginatedStays(variables)

  const items = useMemo(() => {
    const items = (stays ?? []).map(stayToOption)

    if (stay && !items.some(item => item.value === `${stay.id}`)) {
      items.push(stayToOption(stay))
    }

    return items
  }, [stays, stay])

  return (
    <ComboBox
      value={id != null ? `${id}` : ''}
      onChange={value => {
        const id = parseInt(value)
        onChange(isNaN(id) ? null : id)
      }}
      options={items}
      search={search}
      onChangeSearch={onSearch}
      loading={loading}
      invalid={invalid}
      placeholder={placeholder}
      name={name}
      searchText={t`Search Stays`}
    />
  )
}
